import {useCallback, useEffect, useRef} from "react";

export type TaskStatus = {
  status: "processing" | "completed" | "failed";
  task_id: string;
  progress?: number;
  error?: string;
};

export type TaskPollingOptions = {
  pollInterval?: number;
  maxAttempts?: number;
  onProgress?: (progress: number) => void;
  onCompleted: (taskId: string) => Promise<void>;
  onError: (error: TaskPollingError) => void;
};

export class TaskPollingError extends Error {
  code: string;
  originalError?: string;

  constructor(code: string, message: string, originalError?: string) {
    super(message);
    this.name = "TaskPollingError";
    this.code = code;
    this.originalError = originalError;
  }
}

const DEFAULT_POLL_INTERVAL = 2000; // 2 seconds
const DEFAULT_MAX_ATTEMPTS = 150; // 5 minutes

type PollingState = {
  timeoutId: NodeJS.Timeout;
  attempts: number;
};

export const useTaskPolling = () => {
  // Store a separate state for each polling process by task ID
  const pollingStatesRef = useRef<Map<string, PollingState>>(new Map());

  const clearPollTimeout = useCallback((taskId: string) => {
    const state = pollingStatesRef.current.get(taskId);
    if (state) {
      clearTimeout(state.timeoutId);
      pollingStatesRef.current.delete(taskId);
    }
  }, []);

  const pollTaskStatus = useCallback(
    async (statusUrl: string, taskId: string, options: TaskPollingOptions): Promise<void> => {
      const {
        pollInterval = DEFAULT_POLL_INTERVAL,
        maxAttempts = DEFAULT_MAX_ATTEMPTS,
        onProgress,
        onCompleted,
        onError,
      } = options;

      try {
        const state = pollingStatesRef.current.get(taskId);
        const currentAttempts = state ? state.attempts + 1 : 1;

        if (currentAttempts > maxAttempts) {
          clearPollTimeout(taskId);
          throw new TaskPollingError("TIMEOUT", "Operation timeout");
        }

        const response = await fetch(statusUrl);

        if (!response.ok) {
          throw new TaskPollingError("CHECK_FAILED", "Failed to check operation status");
        }

        const statusData: TaskStatus = await response.json();

        if (statusData.status === "completed") {
          clearPollTimeout(taskId);
          await onCompleted(statusData.task_id);
        } else if (statusData.status === "failed") {
          clearPollTimeout(taskId);
          throw new TaskPollingError("PROCESSING_FAILED", "Operation failed", statusData.error);
        } else {
          const progress = statusData.progress || 0;

          if (onProgress) {
            onProgress(progress);
          }

          const timeoutId = setTimeout(() => {
            pollTaskStatus(statusUrl, taskId, options);
          }, pollInterval);

          pollingStatesRef.current.set(taskId, {
            timeoutId,
            attempts: currentAttempts,
          });
        }
      } catch (error) {
        clearPollTimeout(taskId);
        if (error instanceof TaskPollingError) {
          onError(error);
        } else {
          onError(new TaskPollingError("UNKNOWN", error instanceof Error ? error.message : "Unknown error"));
        }
      }
    },
    [clearPollTimeout],
  );

  const startPolling = useCallback(
    (statusUrl: string, taskId: string, options: TaskPollingOptions) => {
      if (pollingStatesRef.current.has(taskId)) {
        clearPollTimeout(taskId);
      }

      pollTaskStatus(statusUrl, taskId, options);
    },
    [pollTaskStatus, clearPollTimeout],
  );

  const stopPolling = useCallback(
    (taskId: string) => {
      clearPollTimeout(taskId);
    },
    [clearPollTimeout],
  );

  const stopAllPolling = useCallback(() => {
    pollingStatesRef.current.forEach((state) => {
      clearTimeout(state.timeoutId);
    });
    pollingStatesRef.current.clear();
  }, []);

  // Cleanup all polling on unmount
  useEffect(() => {
    return () => {
      stopAllPolling();
    };
  }, [stopAllPolling]);

  return {startPolling, stopPolling, stopAllPolling};
};
